"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { MessageSquarePlus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { CurrentUser, ProductSummary, Review } from "@plexstore/types";

import { ReviewForm } from "@/components/store/review-form";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { StarRating } from "@/components/shared/star-rating";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errorMessage } from "@/lib/api/errors";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { ReviewListResponse } from "@/services/server/catalog";
import { reviewService } from "@/services/client";

interface ReviewListProps {
  initial: ReviewListResponse;
  currentUser: CurrentUser | null;
  allowDeletion: boolean;
  minimumLength: number;
  /** When set, the form is bound to one product; otherwise the user picks among `reviewableProducts`. */
  productId?: string;
  reviewableProducts?: ProductSummary[];
  canReview: boolean;
  className?: string;
  showProduct?: boolean;
}

export function ReviewList({ initial, currentUser, minimumLength, productId, reviewableProducts, canReview, className, showProduct }: ReviewListProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Review | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => reviewService.remove(id),
    onSuccess: () => {
      toast.success("Review deleted");
      setPendingDelete(null);
      router.refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className={cn("space-y-6", className)}>
      {canReview && (
        <div className="flex justify-end">
          <Button className="rounded-xl" onClick={() => setOpen(true)}>
            <MessageSquarePlus /> Write a review
          </Button>
        </div>
      )}
      {!currentUser && (
        <p className="text-sm text-text-muted">
          <Link href="/login" className="text-brand underline-offset-4 hover:underline">
            Sign in
          </Link>{" "}
          to leave a review for products you own.
        </p>
      )}

      {initial.items.length === 0 ? (
        <EmptyState title="No reviews yet" description="Be the first to share your experience." />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {initial.items.map((r) => (
            <li key={r.id} className="glass flex flex-col rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <UserAvatar name={r.displayName} src={r.avatarUrl} size={40} />
                  <div className="min-w-0">
                    <Link href={`/profile/${r.userPublicId}`} className="block truncate text-sm font-semibold hover:text-brand">
                      {r.displayName}
                    </Link>
                    <p className="text-xs text-text-muted">{formatRelative(r.createdAt)}</p>
                  </div>
                </div>
                {r.canDelete && (
                  <Button variant="ghost" size="icon-sm" aria-label="Delete review" onClick={() => setPendingDelete(r)}>
                    <Trash2 className="text-danger" />
                  </Button>
                )}
              </div>
              <StarRating value={r.rating} size={14} className="mt-3" />
              <p className="mt-3 text-sm leading-relaxed text-text-secondary">{r.comment}</p>
              {showProduct && (
                <Link href={`/products/${r.productUrlId}`} className="mt-3 text-xs font-medium text-brand hover:underline">
                  {r.productName}
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Write a review</DialogTitle>
          </DialogHeader>
          <ReviewForm
            productId={productId}
            products={reviewableProducts}
            minimumLength={minimumLength}
            onDone={() => {
              setOpen(false);
              router.refresh();
            }}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this review?"
        description="This cannot be undone."
        confirmLabel="Delete"
        destructive
        pending={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </div>
  );
}
